import { CommonModule, NgClass, NgIf } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  computed,
  effect,
  forwardRef,
  inject,
  ViewChild,
  ElementRef,
  signal,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { CountryService } from '../../services';
import { Country } from '../../shared/models';
import {
  OverlayModule,
  CdkOverlayOrigin,
  CdkConnectedOverlay,
  ConnectedPosition,
} from '@angular/cdk/overlay';

@Component({
  selector: 'app-country-select',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    OverlayModule,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
  ],
  templateUrl: './country-select.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CountrySelectComponent),
      multi: true,
    },
  ],
})
export class CountrySelectComponent implements ControlValueAccessor, OnInit {
  @Input() label = 'País';
  @Input() placeholder = 'Selecciona un país';
  @Input() searchPlaceholder = 'Buscar país o código';
  @Input() required = false;
  @Output() countryChange = new EventEmitter<Country | null>();

  private countriesService = inject(CountryService);
  private countriesSignal = this.countriesService.getStore().getModel();

  protected dropdownOpen = signal(false);
  protected searchTerm = signal('');
  private selectedCountryId = signal<string | null>(null);
  protected selectedCountry = signal<Country | null>(null);
  protected disabled = signal(false);
  protected overlayWidth = signal<string | number>('auto');
  protected overlayPositions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 4,
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -4,
    },
  ];

  protected countries = computed(() => this.countriesSignal());
  protected filteredCountries = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const list = this.countries();
    if (!term) return list;
    return list.filter((country) => {
      const target =
        `${country.name} ${country.dialCode} ${country.isoCode}`.toLowerCase();
      return target.includes(term);
    });
  });

  private unregisterEffect = effect(() => {
    const list = this.countries();
    const id = this.selectedCountryId();
    if (!list.length || !id) {
      if (!id) this.selectedCountry.set(null);
      return;
    }
    const match = list.find((c) => c.countryId === id) ?? null;
    this.selectedCountry.set(match);
  });

  @ViewChild(CdkOverlayOrigin, { static: false })
  origin?: CdkOverlayOrigin;

  @ViewChild('searchInput', { static: false })
  searchInput?: ElementRef<HTMLInputElement>;

  @ViewChild('countryList', { static: false })
  countryList?: ElementRef<HTMLDivElement>;

  protected highlightedIndex = signal<number>(-1);

  ngOnInit(): void {
    this.countriesService.fetchAndSetEntitiesToStore();
  }

  writeValue(value: string | null): void {
    this.selectedCountryId.set(value ?? null);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected toggleDropdown(): void {
    if (this.disabled()) return;
    const wasOpen = this.dropdownOpen();
    this.dropdownOpen.set(!wasOpen);
    if (this.dropdownOpen()) {
      this.onTouched();
      this.highlightedIndex.set(-1);
      // Always clear search term when opening dropdown
      this.searchTerm.set('');
      queueMicrotask(() => {
        const width =
          this.origin?.elementRef.nativeElement.getBoundingClientRect().width ??
          null;
        this.overlayWidth.set(width ?? 'auto');
        // Clear and focus search input when dropdown opens
        if (this.searchInput?.nativeElement) {
          this.searchInput.nativeElement.value = '';
          this.searchInput.nativeElement.focus();
        }
      });
    }
  }

  protected onSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchTerm.set(value);
    // Reset highlighted index when search changes
    this.highlightedIndex.set(-1);
  }

  protected selectCountry(country: Country): void {
    if (this.disabled()) return;
    this.selectedCountryId.set(country.countryId);
    this.onChange(country.countryId);
    this.countryChange.emit(country);
    this.dropdownOpen.set(false);
    // Clear search term when selecting
    this.searchTerm.set('');
    this.highlightedIndex.set(-1);
    // Clear search input value
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.value = '';
    }
  }

  protected onButtonKeyDown(event: KeyboardEvent): void {
    if (this.disabled()) return;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!this.dropdownOpen()) {
          this.toggleDropdown();
        }
        break;
      case 'Escape':
        if (this.dropdownOpen()) {
          event.preventDefault();
          this.dropdownOpen.set(false);
          this.highlightedIndex.set(-1);
        }
        break;
      default:
        // If typing a character, open dropdown and focus search input
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          event.preventDefault(); // Prevent the character from being typed in the button
          if (!this.dropdownOpen()) {
            this.toggleDropdown();
          }
          queueMicrotask(() => {
            if (this.searchInput?.nativeElement) {
              this.searchInput.nativeElement.value = event.key;
              this.searchTerm.set(event.key);
              this.searchInput.nativeElement.focus();
              // Set cursor at the end
              this.searchInput.nativeElement.setSelectionRange(1, 1);
            }
          });
        }
        break;
    }
  }

  protected onSearchKeyDown(event: KeyboardEvent): void {
    const filtered = this.filteredCountries();
    const maxIndex = filtered.length - 1;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightedIndex.update((idx) => (idx < maxIndex ? idx + 1 : 0));
        this.scrollToHighlighted();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightedIndex.update((idx) => (idx > 0 ? idx - 1 : maxIndex));
        this.scrollToHighlighted();
        break;
      case 'Enter':
        event.preventDefault();
        if (this.highlightedIndex() >= 0 && this.highlightedIndex() < filtered.length) {
          this.selectCountry(filtered[this.highlightedIndex()]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.dropdownOpen.set(false);
        this.highlightedIndex.set(-1);
        break;
    }
  }

  protected onCountryItemKeyDown(event: KeyboardEvent, country: Country, index: number): void {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.selectCountry(country);
        break;
      case 'ArrowDown':
        event.preventDefault();
        const filtered = this.filteredCountries();
        this.highlightedIndex.set(index < filtered.length - 1 ? index + 1 : 0);
        this.scrollToHighlighted();
        break;
      case 'ArrowUp':
        event.preventDefault();
        const filteredUp = this.filteredCountries();
        this.highlightedIndex.set(index > 0 ? index - 1 : filteredUp.length - 1);
        this.scrollToHighlighted();
        break;
    }
  }

  private scrollToHighlighted(): void {
    queueMicrotask(() => {
      if (this.countryList?.nativeElement && this.highlightedIndex() >= 0) {
        const items = this.countryList.nativeElement.querySelectorAll('button');
        const highlightedItem = items[this.highlightedIndex()] as HTMLElement;
        if (highlightedItem) {
          highlightedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      }
    });
  }

  protected flagEmoji(isoCode?: string): string {
    if (!isoCode) return '🏳️';
    const upper = isoCode.toUpperCase();
    return upper.replace(/[A-Z]/g, (char) =>
      String.fromCodePoint(char.charCodeAt(0) + 127397)
    );
  }

  @HostListener('document:click', ['$event'])
  onOutsideClick(event: MouseEvent) {
    if (!this.dropdownOpen()) return;
    const target = event.target as HTMLElement;
    if (
      !target.closest('.country-select') &&
      !target.closest('.country-select-overlay-panel')
    ) {
      this.dropdownOpen.set(false);
    }
  }

  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};
}
